import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import {
  Box,
  Flex,
  Heading,
  Text,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Icon,
  Spinner,
  Button,
  Tooltip,
  Badge,
} from "@chakra-ui/react";
import { FaCheckCircle, FaExclamationTriangle, FaTimesCircle, FaMinusCircle, FaSyncAlt } from "react-icons/fa";
import { useSupplierRetailer } from "../../../wrappers/SupplierRetailerContext";

const COLUMN_LABELS = {
  sales: "Sales",
  sdc: "SDC",
  strikerate: "Strike Rate",
  dc: "DC",
  articles: "Articles",
};

const STATE_META = {
  ok: { icon: FaCheckCircle, color: "green.500", label: "Updated" },
  warn: { icon: FaExclamationTriangle, color: "orange.400", label: "Getting late" },
  stale: { icon: FaTimesCircle, color: "red.500", label: "Overdue" },
  fail: { icon: FaTimesCircle, color: "red.500", label: "Last file failed" },
  none: { icon: FaMinusCircle, color: "gray.400", label: "No data" },
};

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

// "2026-07-09" or "2026-07-09 06:12" -> "09 Jul"
const shortDate = (str) => {
  if (!str) return "";
  const [d] = str.split(" ");
  const parts = d.split("-");
  if (parts.length !== 3) return str;
  return `${parts[2]} ${MONTHS[parseInt(parts[1], 10) - 1]}`;
};

const StatusCell = ({ cell, mode }) => {
  if (!cell) return <Td textAlign="center">—</Td>;
  const meta = STATE_META[cell.state] || STATE_META.none;

  let subtext = "";
  let tip = meta.label;
  if (mode === "edi") {
    if (cell.state === "ok" && cell.receivedToday) {
      subtext = `${cell.rowsToday} rows`;
      tip = `File processed today ${cell.lastFile?.split(" ")[1] || ""} — ${cell.rowsToday} rows`;
    } else if (cell.state === "fail") {
      subtext = shortDate(cell.lastAttempt);
      tip = `Most recent file FAILED (${cell.lastAttempt}). Last success: ${cell.lastFile || "none"}`;
    } else if (cell.lastFile) {
      subtext = shortDate(cell.lastFile);
      tip = `Last file received ${cell.lastFile}`;
    } else {
      subtext = "";
      tip = "No files in the last 60 days";
    }
  } else {
    if (cell.latestData) {
      subtext = shortDate(cell.lastImport || cell.latestData);
      tip = `Data to ${cell.latestData}${cell.lastImport ? ` · last imported ${cell.lastImport}` : ""}`;
    } else {
      tip = "No data in the last 6 months";
    }
  }

  return (
    <Td textAlign="center" py={1.5}>
      <Tooltip label={tip} fontSize="xs" hasArrow>
        <Box display="inline-block">
          <Icon as={meta.icon} color={meta.color} boxSize={4} display="block" mx="auto" />
          {subtext && (
            <Text fontSize="10px" color="gray.500" lineHeight="1.2">
              {subtext}
            </Text>
          )}
        </Box>
      </Tooltip>
    </Td>
  );
};

const DataStatus = () => {
  const { selectedRetailer } = useSupplierRetailer();
  const [isAdmin, setIsAdmin] = useState(false);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    const userid = localStorage.getItem("userid");
    if (userid === "1" || userid === "2") setIsAdmin(true);
  }, []);

  const fetchStatus = useCallback(
    async (signal) => {
      setLoading(true);
      setError("");
      try {
        const res = await axios.post(
          `${process.env.REACT_APP_API_URL}/data/import-status`,
          { retailer: selectedRetailer, userid: localStorage.getItem("userid") },
          { signal },
        );
        if (res.data.status === 1) setData(res.data.data);
        else setError(res.data.message || "Failed to load import status");
      } catch (e) {
        if (!axios.isCancel(e)) setError(e?.response?.data?.message || e.message);
      } finally {
        setLoading(false);
      }
    },
    [selectedRetailer],
  );

  useEffect(() => {
    if (!isAdmin || !selectedRetailer) return;
    const controller = new AbortController();
    fetchStatus(controller.signal);
    return () => controller.abort();
  }, [isAdmin, selectedRetailer, fetchStatus]);

  if (!isAdmin) {
    return (
      <Box mt={16} ml={8}>
        <Text color="gray.500">This page is only available to administrators.</Text>
      </Box>
    );
  }

  return (
    <Box mt={16} px={8} maxW="900px">
      <Flex align="center" justify="space-between" mb={1}>
        <Flex align="center" gap={3}>
          <Heading size="md">Data Status</Heading>
          {data && (
            <Badge colorScheme="teal" fontSize="sm">
              {data.requestedRetailer || data.retailer}
            </Badge>
          )}
          {data?.sharedFeed && (
            <Badge colorScheme="gray" fontSize="xs">
              shares the Checkers feed
            </Badge>
          )}
        </Flex>
        <Flex align="center" gap={3}>
          {data && (
            <Text fontSize="sm" color="gray.500">
              Today: {data.today}
            </Text>
          )}
          <Button size="sm" leftIcon={<FaSyncAlt />} onClick={() => fetchStatus()} isLoading={loading}>
            Refresh
          </Button>
        </Flex>
      </Flex>
      <Text fontSize="xs" color="gray.500" mb={4}>
        {data?.mode === "edi"
          ? "Green = a file for that feed was processed successfully today (Strike Rate: within 7 days)."
          : "Green = data is as fresh as this retailer's delivery cadence expects."}
      </Text>

      {error && (
        <Text color="red.500" mb={4}>
          {error}
        </Text>
      )}

      {loading && !data ? (
        <Spinner />
      ) : (
        data && (
          <>
            <Table size="sm" border="1px" borderColor="gray.200" borderRadius="md">
              <Thead>
                <Tr>
                  <Th>Supplier</Th>
                  {data.columns.map((c) => (
                    <Th key={c} textAlign="center">
                      {COLUMN_LABELS[c] || c}
                    </Th>
                  ))}
                </Tr>
              </Thead>
              <Tbody>
                {data.rows.map((row) => (
                  <Tr key={row.supplier}>
                    <Td py={1.5} fontSize="sm" fontWeight="medium">
                      {row.supplier}
                    </Td>
                    {data.columns.map((c) => (
                      <StatusCell key={c} cell={row.cells[c]} mode={data.mode} />
                    ))}
                  </Tr>
                ))}
                {data.rows.length === 0 && (
                  <Tr>
                    <Td colSpan={data.columns.length + 1}>
                      <Text color="gray.500">No suppliers linked to this retailer.</Text>
                    </Td>
                  </Tr>
                )}
              </Tbody>
            </Table>

            <Flex mt={3} gap={5} wrap="wrap" fontSize="xs" color="gray.600">
              {Object.entries(STATE_META)
                .filter(([k]) => k !== "fail")
                .map(([k, m]) => (
                  <Flex key={k} align="center" gap={1}>
                    <Icon as={m.icon} color={m.color} boxSize={3} />
                    <Text>{k === "stale" ? "Overdue / failed" : m.label}</Text>
                  </Flex>
                ))}
            </Flex>
          </>
        )
      )}
    </Box>
  );
};

export default DataStatus;
